import { useState, useEffect } from "react";
import { Globe, Plus, RefreshCw, Building2, Users, FileText, Shield, Eye, CheckCircle, XCircle, Briefcase, CalendarCheck, AlertTriangle, Signature, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import axios from "axios";

const STATUS_BADGE = {
  ACTIVE: "bg-green-100 text-green-700",
  PROBATION: "bg-blue-100 text-blue-700",
  TERMINATED: "bg-red-100 text-red-700",
  PENDING: "bg-amber-100 text-amber-700",
  DRAFT: "bg-gray-100 text-gray-700",
  PENDING_SIGNATURE: "bg-amber-100 text-amber-700",
  COMPLETED: "bg-emerald-100 text-emerald-700",
};

export default function SmartPayEOR() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("overview");

  const [dashboard, setDashboard] = useState(null);
  const [entities, setEntities] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [benefits, setBenefits] = useState([]);
  const [visas, setVisas] = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [upcomingCompliance, setUpcomingCompliance] = useState([]);

  const [showNewEntity, setShowNewEntity] = useState(false);
  const [showNewAssignment, setShowNewAssignment] = useState(false);
  const [showNewContract, setShowNewContract] = useState(false);
  const [showNewBenefit, setShowNewBenefit] = useState(false);
  const [showNewVisa, setShowNewVisa] = useState(false);
  const [showNewCompliance, setShowNewCompliance] = useState(false);

  const [newEntity, setNewEntity] = useState({ entity_name: "", country: "", country_code: "", registration_number: "", tax_id: "", entity_type: "LLC", currency: "USD" });
  const [newAssignment, setNewAssignment] = useState({ employee_id: "", employee_name: "", eor_entity_id: "", entity_name: "", job_title: "", department: "", start_date: "", monthly_salary: 0, currency: "USD" });
  const [newContract, setNewContract] = useState({ assignment_id: "", employee_name: "", entity_name: "", country_code: "", job_title: "", monthly_salary: 0, currency: "USD" });
  const [newBenefit, setNewBenefit] = useState({ country: "", benefit_name: "", benefit_type: "HEALTH", benefit_category: "SUPPLEMENTAL", provider_name: "" });
  const [newVisa, setNewVisa] = useState({ employee_name: "", visa_type: "WORK_PERMIT", country: "", issued_date: "", expiry_date: "" });
  const [newCompliance, setNewCompliance] = useState({ entity_code: "", deadline_type: "TAX_FILING", title: "", description: "", due_date: "" });

  const fetchDashboard = async () => { try { const res = await axios.get("/api/smartpay/eor/dashboard"); setDashboard(res.data); } catch {} };
  const fetchEntities = async () => { try { const res = await axios.get("/api/smartpay/eor/entities"); setEntities(res.data.entities || []); } catch {} };
  const fetchAssignments = async () => { try { const res = await axios.get("/api/smartpay/eor/assignments"); setAssignments(res.data.assignments || []); } catch {} };
  const fetchContracts = async () => { try { const res = await axios.get("/api/smartpay/eor/contracts"); setContracts(res.data.contracts || []); } catch {} };
  const fetchBenefits = async () => { try { const res = await axios.get("/api/smartpay/eor/benefits"); setBenefits(res.data.benefits || []); } catch {} };
  const fetchVisas = async () => { try { const res = await axios.get("/api/smartpay/eor/visas"); setVisas(res.data.visas || []); } catch {} };
  const fetchCompliance = async () => { try { const res = await axios.get("/api/smartpay/eor/compliance"); setCompliance(res.data.compliance || []); } catch {} };
  const fetchUpcoming = async () => { try { const res = await axios.get("/api/smartpay/eor/compliance/upcoming?days=30"); setUpcomingCompliance(res.data.compliance || []); } catch {} };

  useEffect(() => { fetchDashboard(); fetchEntities(); fetchAssignments(); fetchContracts(); fetchBenefits(); fetchVisas(); fetchCompliance(); fetchUpcoming(); }, []);

  const handleCreateEntity = async () => {
    try { await axios.post("/api/smartpay/eor/entities", newEntity); toast({ title: "Entity created" }); setShowNewEntity(false); fetchEntities(); } catch { toast({ title: "Failed", variant: "destructive" }); }
  };
  const handleCreateAssignment = async () => {
    try { await axios.post("/api/smartpay/eor/assignments", newAssignment); toast({ title: "Assignment created" }); setShowNewAssignment(false); fetchAssignments(); } catch { toast({ title: "Failed", variant: "destructive" }); }
  };
  const handleCreateContract = async () => {
    try { await axios.post("/api/smartpay/eor/contracts", newContract); toast({ title: "Contract created" }); setShowNewContract(false); fetchContracts(); } catch { toast({ title: "Failed", variant: "destructive" }); }
  };
  const handleCreateBenefit = async () => {
    try { await axios.post("/api/smartpay/eor/benefits", newBenefit); toast({ title: "Benefit created" }); setShowNewBenefit(false); fetchBenefits(); } catch { toast({ title: "Failed", variant: "destructive" }); }
  };
  const handleCreateVisa = async () => {
    try { await axios.post("/api/smartpay/eor/visas", newVisa); toast({ title: "Visa created" }); setShowNewVisa(false); fetchVisas(); } catch { toast({ title: "Failed", variant: "destructive" }); }
  };
  const handleCreateCompliance = async () => {
    try { await axios.post("/api/smartpay/eor/compliance", newCompliance); toast({ title: "Compliance created" }); setShowNewCompliance(false); fetchCompliance(); fetchUpcoming(); } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleTerminate = async (id) => {
    try {
      await axios.post(`/api/smartpay/eor/assignments/${id}/terminate`, { termination_type: "RESIGNATION", termination_date: new Date().toISOString().slice(0, 10), termination_reason: "Voluntary resignation" });
      toast({ title: "Assignment terminated" }); fetchAssignments();
    } catch (e) { toast({ title: e.response?.data?.detail || "Failed", variant: "destructive" }); }
  };

  const handleGenerateContract = async (id) => {
    try {
      await axios.post(`/api/smartpay/eor/contracts/${id}/generate`);
      toast({ title: "Contract generated" }); fetchContracts();
    } catch (e) { toast({ title: e.response?.data?.detail || "Failed", variant: "destructive" }); }
  };

  const handleSignContract = async (id, role) => {
    try {
      await axios.post(`/api/smartpay/eor/contracts/${id}/sign`, { role });
      toast({ title: `Signed as ${role}` }); fetchContracts();
    } catch (e) { toast({ title: e.response?.data?.detail || "Failed", variant: "destructive" }); }
  };

  const kpiCards = [
    { title: "Active Entities", value: dashboard?.active_entities || 0, icon: Building2, color: "text-indigo-600", bg: "bg-indigo-50", border: "border-indigo-200" },
    { title: "Active Assignments", value: dashboard?.active_assignments || 0, icon: Users, color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200" },
    { title: "Pending Visas", value: dashboard?.pending_visas || 0, icon: FileText, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200" },
    { title: "Overdue Compliance", value: dashboard?.overdue_compliance || 0, icon: AlertTriangle, color: "text-red-600", bg: "bg-red-50", border: "border-red-200" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">EOR Suite</h1>
          <p className="text-muted-foreground">Employer of Record — entities, assignments & compliance</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { fetchDashboard(); fetchEntities(); fetchAssignments(); fetchContracts(); fetchBenefits(); fetchVisas(); fetchCompliance(); fetchUpcoming(); }}>
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map(kpi => (
          <Card key={kpi.title} className={`${kpi.bg} ${kpi.border}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{kpi.title}</p>
                  <p className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</p>
                </div>
                <kpi.icon className={`h-8 w-8 ${kpi.color} opacity-70`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="entities">Entities</TabsTrigger>
          <TabsTrigger value="assignments">Assignments</TabsTrigger>
          <TabsTrigger value="contracts">Contracts</TabsTrigger>
          <TabsTrigger value="benefits">Benefits</TabsTrigger>
          <TabsTrigger value="visas">Visas</TabsTrigger>
          <TabsTrigger value="compliance">Compliance</TabsTrigger>
        </TabsList>

        <TabsContent value="entities" className="space-y-4 pt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Legal Entities</CardTitle>
              <Dialog open={showNewEntity} onOpenChange={setShowNewEntity}>
                <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-2" /> New Entity</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Create Entity</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Entity Name</Label><Input value={newEntity.entity_name} onChange={e => setNewEntity({...newEntity, entity_name: e.target.value})} /></div>
                    <div><Label>Country</Label><Input value={newEntity.country} onChange={e => setNewEntity({...newEntity, country: e.target.value})} /></div>
                    <div><Label>Country Code</Label>
                      <Select value={newEntity.country_code} onValueChange={v => setNewEntity({...newEntity, country_code: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="US">US</SelectItem>
                          <SelectItem value="UK">UK</SelectItem>
                          <SelectItem value="ZA">ZA</SelectItem>
                          <SelectItem value="DE">DE</SelectItem>
                          <SelectItem value="SG">SG</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Reg Number</Label><Input value={newEntity.registration_number} onChange={e => setNewEntity({...newEntity, registration_number: e.target.value})} /></div>
                    <div><Label>Tax ID</Label><Input value={newEntity.tax_id} onChange={e => setNewEntity({...newEntity, tax_id: e.target.value})} /></div>
                    <Button onClick={handleCreateEntity}>Create</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Entity</TableHead>
                    <TableHead>Country</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Reg #</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entities.map(e => (
                    <TableRow key={e.id}>
                      <TableCell className="font-medium">{e.entity_name}</TableCell>
                      <TableCell><Badge variant="outline">{e.country_code}</Badge></TableCell>
                      <TableCell>{e.entity_type}</TableCell>
                      <TableCell><Badge className={STATUS_BADGE[e.status]}>{e.status}</Badge></TableCell>
                      <TableCell className="font-mono text-xs">{e.registration_number}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="assignments" className="space-y-4 pt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Assignments</CardTitle>
              <Dialog open={showNewAssignment} onOpenChange={setShowNewAssignment}>
                <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-2" /> New Assignment</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Create Assignment</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Employee Name</Label><Input value={newAssignment.employee_name} onChange={e => setNewAssignment({...newAssignment, employee_name: e.target.value})} /></div>
                    <div><Label>Entity ID</Label><Input value={newAssignment.eor_entity_id} onChange={e => setNewAssignment({...newAssignment, eor_entity_id: e.target.value})} /></div>
                    <div><Label>Job Title</Label><Input value={newAssignment.job_title} onChange={e => setNewAssignment({...newAssignment, job_title: e.target.value})} /></div>
                    <div><Label>Department</Label><Input value={newAssignment.department} onChange={e => setNewAssignment({...newAssignment, department: e.target.value})} /></div>
                    <div><Label>Start Date</Label><Input type="date" value={newAssignment.start_date} onChange={e => setNewAssignment({...newAssignment, start_date: e.target.value})} /></div>
                    <div><Label>Monthly Salary</Label><Input type="number" value={newAssignment.monthly_salary} onChange={e => setNewAssignment({...newAssignment, monthly_salary: parseFloat(e.target.value) || 0})} /></div>
                    <Button onClick={handleCreateAssignment}>Create</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead>Job Title</TableHead>
                    <TableHead>Start Date</TableHead>
                    <TableHead>Salary</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {assignments.map(a => (
                    <TableRow key={a.id}>
                      <TableCell>{a.employee_name}</TableCell>
                      <TableCell className="font-mono text-xs">{a.eor_entity_id?.slice(-8)}</TableCell>
                      <TableCell>{a.job_title}</TableCell>
                      <TableCell>{a.start_date}</TableCell>
                      <TableCell>{a.currency} {a.monthly_salary?.toLocaleString()}</TableCell>
                      <TableCell><Badge className={STATUS_BADGE[a.status]}>{a.status}</Badge></TableCell>
                      <TableCell>
                        {(a.status === "ACTIVE" || a.status === "PROBATION") && (
                          <Button size="sm" variant="destructive" onClick={() => handleTerminate(a.id)}><XCircle className="h-3 w-3 mr-1" /> Terminate</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contracts" className="space-y-4 pt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Employment Contracts</CardTitle>
              <Dialog open={showNewContract} onOpenChange={setShowNewContract}>
                <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-2" /> New Contract</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Create Contract</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Employee Name</Label><Input value={newContract.employee_name} onChange={e => setNewContract({...newContract, employee_name: e.target.value})} /></div>
                    <div><Label>Entity Name</Label><Input value={newContract.entity_name} onChange={e => setNewContract({...newContract, entity_name: e.target.value})} /></div>
                    <div><Label>Country Code</Label>
                      <Select value={newContract.country_code} onValueChange={v => setNewContract({...newContract, country_code: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="US">US</SelectItem>
                          <SelectItem value="UK">UK</SelectItem>
                          <SelectItem value="ZA">ZA</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Job Title</Label><Input value={newContract.job_title} onChange={e => setNewContract({...newContract, job_title: e.target.value})} /></div>
                    <div><Label>Monthly Salary</Label><Input type="number" value={newContract.monthly_salary} onChange={e => setNewContract({...newContract, monthly_salary: parseFloat(e.target.value) || 0})} /></div>
                    <Button onClick={handleCreateContract}>Create</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead>Country</TableHead>
                    <TableHead>Salary</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Signed</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contracts.map(c => (
                    <TableRow key={c.id}>
                      <TableCell>{c.employee_name}</TableCell>
                      <TableCell>{c.entity_name}</TableCell>
                      <TableCell><Badge variant="outline">{c.country_code}</Badge></TableCell>
                      <TableCell>{c.currency} {c.monthly_salary?.toLocaleString()}</TableCell>
                      <TableCell><Badge className={STATUS_BADGE[c.status]}>{c.status}</Badge></TableCell>
                      <TableCell>
                        {c.signed_by_employee && c.signed_by_employer ? <Badge className="bg-green-100 text-green-700">Both</Badge> : c.signed_by_employee ? <Badge variant="outline">Emp</Badge> : c.signed_by_employer ? <Badge variant="outline">Er</Badge> : <Badge variant="secondary">--</Badge>}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {c.status === "DRAFT" && (
                            <Button size="sm" variant="outline" onClick={() => handleGenerateContract(c.id)}><Sparkles className="h-3 w-3 mr-1" /> Generate</Button>
                          )}
                          {!c.signed_by_employee && (
                            <Button size="sm" variant="outline" onClick={() => handleSignContract(c.id, "employee")}><Signature className="h-3 w-3 mr-1" /> Emp</Button>
                          )}
                          {!c.signed_by_employer && (
                            <Button size="sm" variant="outline" onClick={() => handleSignContract(c.id, "employer")}><Signature className="h-3 w-3 mr-1" /> Er</Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="benefits" className="space-y-4 pt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Benefits</CardTitle>
              <Dialog open={showNewBenefit} onOpenChange={setShowNewBenefit}>
                <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-2" /> New Benefit</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Create Benefit</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Benefit Name</Label><Input value={newBenefit.benefit_name} onChange={e => setNewBenefit({...newBenefit, benefit_name: e.target.value})} /></div>
                    <div><Label>Country</Label>
                      <Select value={newBenefit.country} onValueChange={v => setNewBenefit({...newBenefit, country: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="US">US</SelectItem>
                          <SelectItem value="UK">UK</SelectItem>
                          <SelectItem value="ZA">ZA</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Type</Label>
                      <Select value={newBenefit.benefit_type} onValueChange={v => setNewBenefit({...newBenefit, benefit_type: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="HEALTH">Health</SelectItem>
                          <SelectItem value="PENSION">Pension</SelectItem>
                          <SelectItem value="LIFE">Life</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Provider</Label><Input value={newBenefit.provider_name} onChange={e => setNewBenefit({...newBenefit, provider_name: e.target.value})} /></div>
                    <Button onClick={handleCreateBenefit}>Create</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Benefit</TableHead>
                    <TableHead>Country</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Provider</TableHead>
                    <TableHead>Mandatory</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {benefits.map(b => (
                    <TableRow key={b.id}>
                      <TableCell className="font-medium">{b.benefit_name}</TableCell>
                      <TableCell><Badge variant="outline">{b.country}</Badge></TableCell>
                      <TableCell>{b.benefit_type}</TableCell>
                      <TableCell>{b.benefit_category}</TableCell>
                      <TableCell>{b.provider_name}</TableCell>
                      <TableCell>{b.is_mandatory ? <Badge className="bg-red-100 text-red-700">Required</Badge> : <Badge variant="secondary">Opt</Badge>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="visas" className="space-y-4 pt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Visas & Work Permits</CardTitle>
              <Dialog open={showNewVisa} onOpenChange={setShowNewVisa}>
                <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-2" /> New Visa</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Create Visa Record</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Employee Name</Label><Input value={newVisa.employee_name} onChange={e => setNewVisa({...newVisa, employee_name: e.target.value})} /></div>
                    <div><Label>Visa Type</Label>
                      <Select value={newVisa.visa_type} onValueChange={v => setNewVisa({...newVisa, visa_type: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="WORK_PERMIT">Work Permit</SelectItem>
                          <SelectItem value="EMPLOYMENT_VISA">Employment Visa</SelectItem>
                          <SelectItem value="INTRA_COMPANY">Intra-Company Transfer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Country</Label><Input value={newVisa.country} onChange={e => setNewVisa({...newVisa, country: e.target.value})} /></div>
                    <div><Label>Issue Date</Label><Input type="date" value={newVisa.issued_date} onChange={e => setNewVisa({...newVisa, issued_date: e.target.value})} /></div>
                    <div><Label>Expiry Date</Label><Input type="date" value={newVisa.expiry_date} onChange={e => setNewVisa({...newVisa, expiry_date: e.target.value})} /></div>
                    <Button onClick={handleCreateVisa}>Create</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Country</TableHead>
                    <TableHead>Issued</TableHead>
                    <TableHead>Expiry</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visas.map(v => (
                    <TableRow key={v.id}>
                      <TableCell>{v.employee_name}</TableCell>
                      <TableCell>{v.visa_type}</TableCell>
                      <TableCell><Badge variant="outline">{v.country}</Badge></TableCell>
                      <TableCell>{v.issued_date}</TableCell>
                      <TableCell>{v.expiry_date}</TableCell>
                      <TableCell><Badge className={STATUS_BADGE[v.status]}>{v.status}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="compliance" className="space-y-4 pt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Compliance Deadlines</CardTitle>
                  <Dialog open={showNewCompliance} onOpenChange={setShowNewCompliance}>
                    <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-2" /> New Deadline</Button></DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>Create Compliance Deadline</DialogTitle></DialogHeader>
                      <div className="space-y-3">
                        <div><Label>Entity Code</Label><Input value={newCompliance.entity_code} onChange={e => setNewCompliance({...newCompliance, entity_code: e.target.value})} /></div>
                        <div><Label>Title</Label><Input value={newCompliance.title} onChange={e => setNewCompliance({...newCompliance, title: e.target.value})} /></div>
                        <div><Label>Description</Label><Input value={newCompliance.description} onChange={e => setNewCompliance({...newCompliance, description: e.target.value})} /></div>
                        <div><Label>Due Date</Label><Input type="date" value={newCompliance.due_date} onChange={e => setNewCompliance({...newCompliance, due_date: e.target.value})} /></div>
                        <div><Label>Type</Label>
                          <Select value={newCompliance.deadline_type} onValueChange={v => setNewCompliance({...newCompliance, deadline_type: v})}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="TAX_FILING">Tax Filing</SelectItem>
                              <SelectItem value="LABOR_SUBMISSION">Labor Submission</SelectItem>
                              <SelectItem value="BENEFITS_REPORTING">Benefits Reporting</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button onClick={handleCreateCompliance}>Create</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Entity</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {compliance.map(c => (
                        <TableRow key={c.id}>
                          <TableCell className="font-medium">{c.title}</TableCell>
                          <TableCell className="font-mono text-xs">{c.entity_code}</TableCell>
                          <TableCell><Badge variant="outline">{c.deadline_type}</Badge></TableCell>
                          <TableCell>{c.due_date}</TableCell>
                          <TableCell><Badge className={STATUS_BADGE[c.status] || "bg-gray-100"}>{c.status}</Badge></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>

            <div>
              <Card className="bg-amber-50 border-amber-200">
                <CardHeader><CardTitle className="text-amber-800 flex items-center gap-2"><AlertTriangle className="h-5 w-5" /> Upcoming (30d)</CardTitle></CardHeader>
                <CardContent>
                  {upcomingCompliance.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No upcoming deadlines</p>
                  ) : (
                    <div className="space-y-3">
                      {upcomingCompliance.map(u => (
                        <div key={u.id} className="p-2 bg-white rounded border text-sm">
                          <div className="font-medium">{u.title}</div>
                          <div className="flex justify-between text-xs text-muted-foreground mt-1">
                            <span>{u.entity_code}</span>
                            <span className="text-amber-600 font-medium">{u.due_date}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
